/**
 * Where the artwork disagrees with itself across a 3D fold.
 *
 * ## The distinction the edge pipeline does not make
 *
 * Two different faults both show up as "a seam", and only one of them is ours
 * to fix:
 *
 * | | Cause | Who fixes it |
 * |---|---|---|
 * | **Bare across the fold** | one side carries paint, the other stops | `seamBleed` — this is exactly its job |
 * | **A disagreement** | both sides are painted and they do not match | the artist, in whatever they drew it in |
 *
 * `seamBlend` currently treats the second as if it were the first: it averages
 * the two sides over a six-texel band. That closes a hairline caused by
 * sampling, and it cannot close a red edge meeting a blue one — it turns the
 * break into a gradient. Softer, not gone, which is precisely the complaint
 * that motivated this file.
 *
 * So this measures rather than corrects. A disagreement is a fact about the
 * artwork, and the useful thing to do with it is say where it is, in words
 * naming a place on a garment, so it can be removed at the source. Experienced
 * Roblox designers already draw this way — they do not let a feature cross a
 * fold with different colour on each side — and the whole class of seam simply
 * does not arise for them.
 *
 * ## Measured at the rim only
 *
 * Depth 0: the outermost row of each region. That is the row that ends up
 * adjacent to its neighbour on the body, so it is the only row where a
 * mismatch is visible as a break. Looking deeper finds disagreements that are
 * genuinely just the design changing across a face, and reporting those would
 * bury the real ones.
 *
 * ## Direction
 *
 * `across` is one-way in practice — `edge.ts` records `torso.right` sending
 * 1,792 links into `torso.front` and nothing coming back — so a fold is keyed
 * on its two faces sorted, and both directions accumulate into one entry.
 * Otherwise the same physical seam reports twice with different totals.
 */

import { CANVAS, type GarmentType, REGION_IDS, type RegionId } from "./format";
import { regionFacts } from "./region-facts";
import { REGIONS, regionAt } from "./regions";
import type { SeamMap } from "./seam-map";

export interface Bitmap {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

/** One fold, and how badly the artwork breaks across it. */
export interface SeamBreak {
  /** The two faces that meet, sorted, so a fold has one identity. */
  faces: [RegionId, RegionId];
  /** Rim pairs where both sides carry paint — the ones that could be judged. */
  compared: number;
  /** Of those, how many differ by more than the threshold. */
  broken: number;
  /** Mean difference over the broken pairs, 0–255 in the worst channel. */
  severity: number;
  /** The single worst difference on this fold, 0–255. */
  worst: number;
  /** Where that worst pair sits, for pointing at it on the template. */
  at: { x: number; y: number };
}

export interface SeamCheck {
  /** Rim pairs where both sides carry paint. */
  compared: number;
  /** Of those, how many disagree. */
  broken: number;
  /**
   * Rim pairs where exactly one side carries paint.
   *
   * Not a fault in the artwork — `seamBleed` fills these, and drawing to the
   * very edge of every face is not something anyone should be asked to do.
   * Counted so the two causes stay separable in the report.
   */
  bare: number;
  /** Worst fold first. Empty when the artwork agrees everywhere. */
  breaks: SeamBreak[];
  /**
   * Texel indices of every broken pair, BOTH ends, or null unless asked for.
   *
   * The overlay draws these directly: a fold broken for eight texels is marked
   * for eight, rather than the whole edge being lit because part of it
   * disagrees. Both ends because a fold is two rectangles far apart on the
   * template, and marking only one hides half the problem.
   *
   * Off by default. The export sheet wants counts and names, not 13,000
   * positions, and allocating them per commit for a reader that discards them
   * is the kind of cost that is invisible until it is not.
   */
  marks: Int32Array | null;
}

export interface SeamCheckOptions {
  /**
   * How far two rim texels may differ before it counts as a break, 0–255 in
   * the worst of the four channels.
   *
   * A judgement, not a measurement, and deliberately not tight. Antialiasing,
   * JPEG history and a soft airbrush all put a few levels between neighbours
   * that read as continuous; a hard break between two different design
   * elements is typically a whole channel apart. 24 is about 9%, which sits
   * well clear of the first and well below the second.
   */
  threshold?: number;
  /** Alpha at or above which a texel counts as painted. */
  opaque?: number;
  /** Collect the broken texel positions. See `SeamCheck.marks`. */
  marks?: boolean;
}

const DEFAULT_THRESHOLD = 24;
/*
  Low, because the question here is "did the artist put anything here", not
  "is it solid". A feathered edge at alpha 20 is still artwork, and treating it
  as bare would file a real disagreement under `bare` where nobody looks.
*/
const DEFAULT_OPAQUE = 16;

/** The largest per-channel gap between two texels, alpha included. */
function difference(d: Uint8ClampedArray, i: number, j: number): number {
  let worst = 0;
  for (let c = 0; c < 4; c++) {
    const gap = Math.abs((d[i * 4 + c] as number) - (d[j * 4 + c] as number));
    if (gap > worst) worst = gap;
  }
  return worst;
}

/** A fold's identity, independent of which way the link points. */
function foldKey(a: RegionId, b: RegionId): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * The rim links of a seam map, flattened, so a scan does not have to find them.
 *
 * Only 6,584 of a map's 327,015 entries are rim links, and walking the whole
 * array to locate them is 5.59ms — 28x the 0.198ms it takes to scan the links
 * once they are known, and a third of a frame the overlay cannot afford on
 * every stroke. Measured on r6-classic/shirt.
 *
 * Built once per map, which is the same lifetime the map itself has.
 */
export interface SeamIndex {
  /** Flat pairs: [from, to, from, to, …]. */
  links: Int32Array;
  width: number;
  height: number;
}

export function prepareSeamIndex(seam: SeamMap): SeamIndex {
  const rim = rimTexels(seam.width, seam.height);
  const pairs: number[] = [];
  for (let i = 0; i < seam.across.length; i++) {
    const to = seam.across[i] ?? -1;
    // The rim row only, both ends: a link from a deep texel describes a
    // continuation nobody can see a break in.
    if (to < 0 || !rim[i] || !rim[to]) continue;
    pairs.push(i, to);
  }
  return { links: Int32Array.from(pairs), width: seam.width, height: seam.height };
}

function isIndex(v: SeamMap | SeamIndex): v is SeamIndex {
  return (v as SeamIndex).links !== undefined;
}

/**
 * Compare both sides of every fold and report where they do not match.
 *
 * Takes either a map or a prepared index. The map form builds an index and
 * throws it away, which is right for the export — it runs once — and wrong for
 * the overlay, which runs on every commit and should hold onto one.
 *
 * The two forms must not diverge, so there is only one scan: the map form is
 * `prepareSeamIndex` followed by the same loop. `seam-check.test.ts` asserts
 * they agree anyway, because a fast path beside a reference implementation is
 * the exact shape of the bug family this codebase keeps producing — a second
 * route to the pixels that nobody told about the rule.
 */
export function checkSeams(
  b: Bitmap,
  seam: SeamMap | SeamIndex,
  opts: SeamCheckOptions = {},
): SeamCheck {
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  const opaque = opts.opaque ?? DEFAULT_OPAQUE;
  const empty: SeamCheck = { compared: 0, broken: 0, bare: 0, breaks: [], marks: null };
  if (seam.width !== b.width || seam.height !== b.height) return empty;

  const index = isIndex(seam) ? seam : prepareSeamIndex(seam);
  const { width, data } = b;

  interface Acc {
    faces: [RegionId, RegionId];
    compared: number;
    broken: number;
    total: number;
    worst: number;
    at: { x: number; y: number };
  }
  const folds = new Map<string, Acc>();
  const marks: number[] | null = opts.marks ? [] : null;
  let compared = 0;
  let broken = 0;
  let bare = 0;

  for (let k = 0; k < index.links.length; k += 2) {
    const i = index.links[k] as number;
    const to = index.links[k + 1] as number;

    const hereLit = (data[i * 4 + 3] as number) >= opaque;
    const thereLit = (data[to * 4 + 3] as number) >= opaque;
    if (!hereLit && !thereLit) continue;
    if (hereLit !== thereLit) {
      bare++;
      continue;
    }

    const a = regionAt(i % width, Math.floor(i / width));
    const c = regionAt(to % width, Math.floor(to / width));
    if (!a || !c || a === c) continue;

    compared++;
    const gap = difference(data, i, to);
    const key = foldKey(a, c);
    let acc = folds.get(key);
    if (!acc) {
      acc = {
        faces: a < c ? [a, c] : [c, a],
        compared: 0,
        broken: 0,
        total: 0,
        worst: -1,
        at: { x: 0, y: 0 },
      };
      folds.set(key, acc);
    }
    acc.compared++;
    if (gap > acc.worst) {
      acc.worst = gap;
      acc.at = { x: i % width, y: Math.floor(i / width) };
    }
    if (gap <= threshold) continue;
    broken++;
    acc.broken++;
    acc.total += gap;
    if (marks) marks.push(i, to);
  }

  const breaks: SeamBreak[] = [];
  for (const acc of folds.values()) {
    if (acc.broken === 0) continue;
    breaks.push({
      faces: acc.faces,
      compared: acc.compared,
      broken: acc.broken,
      severity: acc.total / acc.broken,
      worst: acc.worst,
      at: acc.at,
    });
  }
  /*
    By how much of the fold is broken, not by how hard the break is.

    A two-texel disagreement at full contrast is a stray pixel; a whole edge out
    by 40 levels is the one that reads as a seam on the body. Sorting by
    severity alone put the stray first every time it was tried.
  */
  breaks.sort((x, y) => y.broken - x.broken || y.severity - x.severity);

  return { compared, broken, bare, breaks, marks: marks ? Int32Array.from(marks) : null };
}

/**
 * Which texels sit on the outer row of a region.
 *
 * `edge.ts` has `rimDepths`, which answers a richer question and is cached for
 * the blend's benefit. This one is a bitmask of depth 0 and is cheap enough to
 * build per call — the check runs on import and on export, not per frame.
 */
function rimTexels(width: number, height: number): Uint8Array {
  const rim = new Uint8Array(width * height);
  for (const id of REGION_IDS) {
    const r = REGIONS[id];
    for (let y = r.y; y < r.y + r.h; y++) {
      if (y < 0 || y >= height) continue;
      const edgeRow = y === r.y || y === r.y + r.h - 1;
      for (let x = r.x; x < r.x + r.w; x++) {
        if (x < 0 || x >= width) continue;
        if (edgeRow || x === r.x || x === r.x + r.w - 1) rim[y * width + x] = 1;
      }
    }
  }
  return rim;
}

/**
 * A fold in words, for somebody who has never opened a template.
 *
 * "Chest / Right sleeve · outer" rather than "torso.front|rightLimb.right",
 * because the whole point of this check is that the fix happens somewhere else
 * — in Photoshop, on a canvas with no region names on it.
 */
export function describeSeamBreak(brk: SeamBreak, garment: GarmentType): string {
  const [a, b] = brk.faces;
  return `${regionFacts(a, garment).label} / ${regionFacts(b, garment).label}`;
}

/** Whether the template this check assumes matches the bitmap it was given. */
export function seamCheckApplies(b: Bitmap): boolean {
  return b.width === CANVAS.width && b.height === CANVAS.height;
}
